import { useMemo } from "react";
import { z } from "zod";
import { useLingui } from "@lingui/react/macro";
import { Trans } from "@lingui/react";
import { AlertTriangle, AlertCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { BaseEditableForm } from "@/components/form/BaseEditableForm";
import type { FieldConfig, GroupConfig } from "@/components/form/types";
import { useCompleteSettlingIn, useUpdateCompleteSettlingIn, useUndoCompleteSettlingIn, useCompletionWarnings, useSettlingInBillingPreview } from "@/hooks/useSettlingIn";
import { BillingPreviewBlock } from "@/components/settling-in/BillingPreviewBlock";
import { settlingin_CompletePlanWarningDTO } from "@/types/settlingIn";
import { toOrgDateOnly } from "@/utils/dateUtils";
import { format } from "date-fns";
import { typeError } from "@/lib/zodTypeError";

interface CompleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planId: string;
  endDate?: string;
  isCompleted?: boolean;
  /**
   * The plan's PERSISTED sign-off date, forwarded to the billing preview as
   * `as_of` so the target invoice period resolves. Omitted when the plan is not
   * yet signed off — the period is then genuinely unknown, not "this month".
   *
   * Accepts either a bare `yyyy-MM-dd` or a full ISO timestamp; the value is
   * reduced to `yyyy-MM-dd` before it reaches the query, because the handler
   * parses `as_of` with the `2006-01-02` layout and rejects anything else
   * with a 400.
   */
  signOffDate?: string;
}

interface CompleteFormData extends Record<string, unknown> {
  end_date: string;
  acknowledged?: boolean;
}

/**
 * What the CONTROLS hold, before the schema's transform runs: the date field accepts
 * either a picker `Date` or an already-formatted string. `BaseEditableForm`'s second
 * generic is exactly this input side (`z.ZodType<Output, _, Input>`).
 */
interface CompleteFormInput extends Record<string, unknown> {
  end_date: string | Date;
  acknowledged?: boolean;
}

export function CompleteDialog({ open, onOpenChange, planId, endDate, isCompleted, signOffDate }: CompleteDialogProps) {
  const { t } = useLingui();
  const completeMutation = useCompleteSettlingIn();
  const updateCompleteMutation = useUpdateCompleteSettlingIn();
  const undoCompleteMutation = useUndoCompleteSettlingIn();
  const warningsQuery = useCompletionWarnings(planId, open && !isCompleted);
  /**
   * #2222 (Sven, 2026-10-25): the family signs off AFTER the completion, so a
   * missing sign-off is the expected next step, not a warning to acknowledge. It
   * renders as an info line and stays out of D5's acknowledgment.
   */
  const signOffNext = (warningsQuery.data?.warnings ?? []).some(
    (w) => w.type === settlingin_CompletePlanWarningDTO.type.SIGN_OFF_MISSING,
  );
  const warnings = useMemo(
    () =>
      (warningsQuery.data?.warnings ?? []).filter(
        (w) => w.type !== settlingin_CompletePlanWarningDTO.type.SIGN_OFF_MISSING,
      ),
    [warningsQuery.data],
  );
  /**
   * A FAILED warnings fetch is not an empty warning set. `data` is `undefined`
   * in both cases, so the `?? []` above collapses them — and on the error path
   * `isLoading` is false too, so the in-flight guard below does not catch it
   * either. Left unhandled, a 500 on this query silently drops D5's
   * acknowledgment: the user completes the plan having never been shown the
   * warnings. Track the error separately and refuse to complete until the
   * warning set is actually known.
   */
  const warningsFailed = !isCompleted && warningsQuery.isError;

  /**
   * Completion can be the PAIR-COMPLETING action: billing is automatic once
   * completion and sign-off both exist, so this dialog must never be confirmed
   * blind. Read-only and advisory — it takes no lock.
   */
  const previewQuery = useSettlingInBillingPreview(
    planId,
    open && !isCompleted,
    signOffDate ? toOrgDateOnly(signOffDate) || undefined : undefined,
  );

  /**
   * Decision D5's soft gate: when advisory warnings exist on the create path,
   * submission takes ONE explicit acknowledgment.
   *
   * Gated through the form's own Zod schema rather than a local `acknowledged`
   * state, because `BaseEditableForm` exposes no `submitDisabled` prop — a local
   * boolean would have nothing to bind to, leaving only an `isSubmitting` abuse
   * (which disables every field and flips the button to a spinner), a shared-form
   * platform change (out of blast radius), or a silent `onSubmit` early-return
   * (the exact silent failure D5 forbids). zodResolver blocks submit with an
   * inline, localized message instead: visible feedback, never a hard block —
   * the checkbox is always tickable, and ticking it once always permits submit.
   */
  const needsAck = !isCompleted && warnings.length > 0;

  const dateRequiredMsg = t({ id: "settlingIn.complete.validation.dateRequired", message: "End date is required" });
  const ackRequiredMsg = t({
    id: "settlingIn.complete.validation.acknowledgeRequired",
    message: "Please confirm you want to record this despite the warnings",
  });

  const schema = useMemo(() => z.object({
    end_date: z.union([
      z.string().min(1, dateRequiredMsg),
      z.date({ error: typeError({ required: dateRequiredMsg }) }),
    ]).transform((val) => {
      if (val instanceof Date) return format(val, "yyyy-MM-dd");
      return val;
    }),
    // Declared unconditionally (relaxed to an optional boolean when the
    // acknowledgement is not required) rather than conditionally spread: an optional
    // KEY in the shape types the parsed value as `unknown`, which no form field type
    // can accept. The field is only rendered — and only validated as `literal(true)`
    // — when `needsAck`, so nothing changes for the user.
    acknowledged: needsAck
      ? z.literal(true, { error: () => ackRequiredMsg })
      : z.boolean().optional(),
  }), [dateRequiredMsg, needsAck, ackRequiredMsg]);

  const fields = useMemo<FieldConfig<CompleteFormData>[]>(() => [
    {
      name: "end_date",
      label: t({ id: "settlingIn.complete.endDate", message: "End Date" }),
      type: "date",
      required: true,
    },
    ...(needsAck
      ? [{
          name: "acknowledged" as const,
          label: t({
            id: "settlingIn.complete.recordAnyway",
            message: "Record anyway despite the warnings above",
          }),
          type: "boolean" as const,
          required: true,
        }]
      : []),
  ], [t, needsAck]);

  const groups = useMemo<GroupConfig<CompleteFormData>[]>(() => [
    {
      title: t({ id: "settlingIn.complete.groupTitle", message: "Completion Details" }),
      description: isCompleted
        ? undefined
        : t({ id: "settlingIn.complete.groupDescription", message: "No further schedule, tracking, or progress entries can be added after completion." }),
      fields: needsAck ? ["end_date", "acknowledged"] : ["end_date"],
    },
  ], [t, isCompleted, needsAck]);

  const defaultValues = useMemo<Partial<CompleteFormData>>(() => ({
    // `new Date("2026-03-15")` parses as UTC midnight, so `format` in a browser
    // at a negative offset renders the PREVIOUS day and silently back-dates the
    // completion. `toOrgDateOnly` passes a bare date through untouched.
    end_date: endDate ? toOrgDateOnly(endDate) : format(new Date(), "yyyy-MM-dd"),
    acknowledged: false,
  }), [endDate]);

  const handleSubmit = async (values: CompleteFormData) => {
    if (isCompleted) {
      await updateCompleteMutation.mutateAsync({
        id: planId,
        data: { end_date: values.end_date },
      });
    } else {
      await completeMutation.mutateAsync({
        id: planId,
        data: { end_date: values.end_date },
      });
    }
    onOpenChange(false);
  };

  const handleDelete = async () => {
    await undoCompleteMutation.mutateAsync(planId);
    onOpenChange(false);
  };

  /**
   * Close the warnings-in-flight bypass: the query starts in flight, so
   * `warnings` is `[]` for the first paint and a user submitting before it
   * settles would skip the acknowledgment entirely, silently defeating D5.
   * Disabling submit until the warning set is known is this prop's legitimate
   * use — submission genuinely is not yet permitted. The edit path
   * (`isCompleted`) never fetches warnings and is unaffected.
   *
   * Gate on `status !== "success"`, NOT on `isLoading || isError`. Those two miss
   * the disabled query: `useCompletionWarnings` also gates on `isSignedIn &&
   * isAuthReady`, and a disabled query sits at status `pending` with fetchStatus
   * `idle` — so `isLoading` (pending AND fetching) is false while `warnings` is
   * still `[]`. Submit would have opened before auth settled and dropped the
   * acknowledgment. `success` is the only state in which the warning set is
   * actually known.
   *
   * The billing preview gates submission on exactly the same terms, and for the
   * same reason: completion can be the PAIR-COMPLETING action, so confirming it
   * while the preview is failed, disabled, or still in flight is the "billing
   * action performed blind" the preview exists to prevent. Rendering the failure
   * (BillingPreviewBlock does) is not the same as refusing the action.
   *
   * `status !== "success"` covers all three unready states at once — errored,
   * loading, and the DISABLED query (pending + idle, which `isLoading` misses).
   * `isFetching` additionally covers a retry/refetch over already-cached data,
   * where `status` stays `success` while the figure on screen is being replaced.
   *
   * The disabled-query tension resolves cleanly because the preview is disabled
   * on exactly the branches where the gate does not apply: `enabled` is
   * `open && !isCompleted`, and this clause is itself scoped to `!isCompleted`.
   * A closed dialog cannot be submitted. So a deliberately-disabled preview never
   * wedges a reachable button — and BillingPreviewBlock's Retry stays the way out
   * of the errored state, re-enabling submit as soon as the preview succeeds.
   */
  const previewNotReady =
    !isCompleted && (previewQuery.status !== "success" || previewQuery.isFetching);

  const isSubmitting =
    completeMutation.isPending ||
    updateCompleteMutation.isPending ||
    (!isCompleted && warningsQuery.status !== "success") ||
    previewNotReady;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isCompleted
              ? <Trans id="settlingIn.complete.editTitle">Edit Completion</Trans>
              : <Trans id="settlingIn.complete.title">Complete Settling-In</Trans>
            }
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans id="common.dialog.formDescription" message="Fill in the fields, then save." />
          </DialogDescription>
        </DialogHeader>

        {!isCompleted && (
          <BillingPreviewBlock
            data={previewQuery.data}
            isLoading={previewQuery.isLoading}
            isError={previewQuery.isError}
            isFetching={previewQuery.isFetching}
            onRetry={() => void previewQuery.refetch()}
          />
        )}

        {warningsFailed && (
          <div
            className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
            role="alert"
            data-testid="completion-warnings-error"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="space-y-2">
              <span>
                <Trans id="settlingIn.complete.warning.loadFailed">
                  The completion warnings could not be loaded, so this plan cannot be completed yet. Retry to continue.
                </Trans>
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void warningsQuery.refetch()}
                disabled={warningsQuery.isFetching}
              >
                <Trans id="common.retry">Try again</Trans>
              </Button>
            </div>
          </div>
        )}

        {!isCompleted && signOffNext && (
          <div
            className="flex items-start gap-2 rounded-md border p-3 text-sm text-muted-foreground"
            data-testid="completion-sign-off-next"
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              <Trans
                id="settlingIn.complete.info.signOffNext"
                message="Next, the family confirms the settling-in in the parent app. Any settling-in fee is billed on the sign-off date."
              />
            </span>
          </div>
        )}

        {!isCompleted && warnings.length > 0 && (
          <div className="space-y-2 py-2" data-testid="completion-warnings">
            {warnings.map((warning) => (
              <div key={warning.type} className="flex items-start gap-2 rounded-md border border-warning-on-surface/40 bg-warning-muted p-3 text-sm text-warning-on-surface">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>
                  {warning.type === settlingin_CompletePlanWarningDTO.type.FUTURE_SCHEDULE_ENTRIES && (
                    <Trans
                      id="settlingIn.complete.warning.futureEntries"
                      values={{ count: warning.count ?? 0 }}
                      message="{count, plural, one {There is # future schedule entry beyond today} other {There are # future schedule entries beyond today}}"
                    />
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        <BaseEditableForm<CompleteFormData, CompleteFormInput>
          schema={schema}
          fields={fields}
          groups={groups}
          defaultValues={defaultValues}
          mode={isCompleted ? "edit" : "create"}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          onDelete={isCompleted ? handleDelete : undefined}
          isSubmitting={isSubmitting}
          isDeleting={undoCompleteMutation.isPending}
          inSheet
        />
      </DialogContent>
    </Dialog>
  );
}
